import { useEffect, useRef, useState } from 'react';
import { Button, Group, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { sha256Hex } from '@prooffleet/client';
import { getBlob, putBlob } from '../offline/db.js';

/**
 * Голосовая заметка к пункту осмотра.
 *
 * Требование CLAUDE.md из раздела про UX водителя: диктовать удобнее, чем
 * печатать, когда на руках перчатки, а вторая рука держит фонарь. Распознавание
 * не делается — это вложение к записи, а не текст.
 *
 * Заметка проходит тем же путём, что снимок: локальный blob → presign →
 * прямая загрузка в хранилище, и попадает в сверку хешей на сервере.
 */
export function VoiceNote({
  draftId,
  audioId,
  onChange,
}: {
  draftId: string;
  audioId: string | null;
  onChange: (audioId: string | null) => void;
}): React.JSX.Element | null {
  const { t } = useTranslation();
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  const supported =
    typeof MediaRecorder !== 'undefined' && navigator.mediaDevices?.getUserMedia !== undefined;

  // Проигрывание берётся из локального хранилища: пока осмотр не отправлен,
  // сервера у записи ещё нет, а водитель должен иметь возможность переслушать.
  useEffect(() => {
    let revoked: string | null = null;
    if (audioId === null) {
      setUrl(null);
      return undefined;
    }
    void getBlob(audioId).then((blob) => {
      if (blob === undefined) return;
      revoked = URL.createObjectURL(new Blob([blob.bytes], { type: blob.mime }));
      setUrl(revoked);
    });
    return () => {
      if (revoked !== null) URL.revokeObjectURL(revoked);
    };
  }, [audioId]);

  useEffect(() => {
    if (!recording) return undefined;
    const timer = setInterval(() => setSeconds((n) => n + 1), 1000);
    return () => clearInterval(timer);
  }, [recording]);

  if (!supported) return null;

  const start = async (): Promise<void> => {
    setFailed(false);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // webm/opus поддержан везде, где есть MediaRecorder, кроме Safari —
      // там пишется mp4, поэтому расширение выводим из типа, а не задаём.
      const recorder = new MediaRecorder(stream);
      chunksRef.current = [];
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) chunksRef.current.push(event.data);
      };
      recorder.onstop = () => {
        stream.getTracks().forEach((track) => {
          track.stop();
        });
        void save(new Blob(chunksRef.current, { type: recorder.mimeType }));
      };
      recorderRef.current = recorder;
      recorder.start();
      setSeconds(0);
      setRecording(true);
    } catch {
      setFailed(true);
    }
  };

  const save = async (blob: Blob): Promise<void> => {
    const bytes = new Uint8Array(await blob.arrayBuffer());
    const mime = blob.type === '' ? 'audio/webm' : blob.type.split(';')[0]!;
    const localId = crypto.randomUUID();

    await putBlob({
      localId,
      draftId,
      bytes: bytes.slice().buffer,
      sha256: await sha256Hex(bytes),
      mime,
      extension: mime.includes('mp4') ? 'mp4' : mime.includes('ogg') ? 'ogg' : 'webm',
      objectKey: null,
      versionId: null,
    });
    onChange(localId);
  };

  const stop = (): void => {
    recorderRef.current?.stop();
    recorderRef.current = null;
    setRecording(false);
  };

  return (
    <div>
      <Group grow gap="sm">
        <Button
          variant={recording ? 'filled' : 'default'}
          {...(recording ? { color: 'red' } : {})}
          onClick={() => (recording ? stop() : void start())}
        >
          {recording
            ? `${t('voice.stop')} · ${String(seconds)}s`
            : audioId === null
              ? t('voice.record')
              : t('voice.rerecord')}
        </Button>
      </Group>

      {url !== null && !recording && (
        <audio src={url} controls style={{ width: '100%', marginTop: 10 }}>
          <track kind="captions" />
        </audio>
      )}

      {failed && (
        <Text c="red" fz="sm" mt={6}>
          {t('voice.denied')}
        </Text>
      )}
    </div>
  );
}
